import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { getMe, type Me } from '../../api/me'
import { publicApi } from '../../lib/api'
import { getToken } from '../../lib/auth'
import WalletSettings from './Wallet'

type HandoffIntentResponse = {
  intent_user_id?: number | null
  intent_handle?: string
}

function userHandle(me: Me | null): string {
  const value = String(me?.profile?.handle || me?.username || '').trim()
  return value ? `@${value}` : '@unknown'
}

export default function WalletBuy() {
  const authed = Boolean(getToken())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [me, setMe] = useState<Me | null>(null)
  const [intent, setIntent] = useState<HandoffIntentResponse | null>(null)
  const [allowMismatch, setAllowMismatch] = useState(false)

  const loadIdentity = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [mePayload, intentPayload] = await Promise.all([
        getMe(),
        publicApi.get<HandoffIntentResponse>('/api/auth/handoff-intent/').then((r) => r.data).catch(() => ({} as HandoffIntentResponse)),
      ])
      setMe(mePayload)
      setIntent(intentPayload || {})
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Unable to verify account.')
      setMe(null)
      setIntent(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!authed) {
      setLoading(false)
      setMe(null)
      setIntent(null)
      return
    }
    void loadIdentity()
  }, [authed, loadIdentity])

  const mismatch = useMemo(() => {
    const intended = Number(intent?.intent_user_id || 0)
    const actual = Number(me?.id || 0)
    if (!intended || !actual) return false
    return intended !== actual
  }, [intent?.intent_user_id, me?.id])

  if (!authed) {
    return (
      <section className="mx-auto max-w-xl rounded-xl border border-white/10 bg-white/[0.03] p-5 text-white">
        <h1 className="text-xl font-semibold">Buy PIXE</h1>
        <p className="mt-2 text-white/70">Sign in to continue to wallet checkout.</p>
        <Link
          to="/login?next=%2Fsettings%2Fwallet%2Fbuy"
          className="mt-4 inline-flex min-h-11 items-center rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15"
        >
          Sign in
        </Link>
      </section>
    )
  }

  if (loading) {
    return (
      <section className="mx-auto max-w-xl rounded-xl border border-white/10 bg-white/[0.03] p-5 text-white">
        <h1 className="text-xl font-semibold">Buy PIXE</h1>
        <p className="mt-2 text-white/70">Verifying active account...</p>
      </section>
    )
  }

  if (error || !me) {
    return (
      <section className="mx-auto max-w-xl rounded-xl border border-rose-400/30 bg-rose-500/10 p-5 text-white">
        <h1 className="text-xl font-semibold">Buy PIXE</h1>
        <p className="mt-2 text-rose-100">{error || 'Unable to verify account.'}</p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => void loadIdentity()}
            className="inline-flex min-h-11 items-center rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15"
          >
            Retry
          </button>
          <Link
            to="/login?next=%2Fsettings%2Fwallet%2Fbuy"
            className="inline-flex min-h-11 items-center rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15"
          >
            Sign in
          </Link>
        </div>
      </section>
    )
  }

  if (mismatch && !allowMismatch) {
    const intended = String(intent?.intent_handle || '').trim()
    return (
      <section className="mx-auto max-w-xl rounded-xl border border-amber-400/30 bg-amber-500/10 p-5 text-white">
        <h1 className="text-xl font-semibold">Confirm account before checkout</h1>
        <p className="mt-2 text-amber-100">
          Browser intent was for {intended ? `@${intended}` : 'another account'}, but current web session is {userHandle(me)}.
        </p>
        <p className="mt-2 text-amber-50/80">Switch accounts before continuing, or explicitly continue with the current account.</p>
        <div className="mt-4 flex gap-2">
          <Link
            to="/login?next=%2Fsettings%2Fwallet%2Fbuy"
            className="inline-flex min-h-11 items-center rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15"
          >
            Switch account
          </Link>
          <button
            type="button"
            onClick={() => setAllowMismatch(true)}
            className="inline-flex min-h-11 items-center rounded-lg border border-amber-300/50 bg-amber-500/20 px-4 py-2 text-sm font-semibold text-amber-100 hover:bg-amber-500/30"
          >
            Continue as {userHandle(me)}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="space-y-3">
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <div className="text-xs uppercase tracking-wide text-white/60">Account confirmation</div>
        <div className="mt-1 text-sm text-white/90">
          You are buying PIXE for <span className="font-semibold">{userHandle(me)}</span>.
        </div>
      </div>
      <WalletSettings initialView="buy" buyCloseTo="/settings/wallet?tab=activity" />
    </section>
  )
}
